import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { preRegistrationsApi } from "../api/preRegistrations";
import { qk } from "../../../../lib/queryKeys";
import { useClientScope } from "../../../../shared/contexts";

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const PreRegistrations: React.FC = () => {
  const queryClient = useQueryClient();
  const { isCentralAdmin, effectiveClientId } = useClientScope();
  const [answersOpen, setAnswersOpen] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: qk.preRegistrations(effectiveClientId ?? null),
    queryFn: () => preRegistrationsApi.list(effectiveClientId),
  });

  const answersQuery = useQuery({
    queryKey: ["pre-registration-answers", selectedEmail],
    queryFn: () =>
      selectedEmail ? preRegistrationsApi.getAnswers(selectedEmail) : null,
    enabled: Boolean(selectedEmail),
  });

  const approveMutation = useMutation({
    mutationFn: (email: string) => preRegistrationsApi.approve(email),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.preRegistrations(effectiveClientId ?? null),
      });
    },
  });

  return (
    <Box>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Pre-Registrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Approve pending registrations to create accounts and send onboarding emails.
          </Typography>
          {!isCentralAdmin && !effectiveClientId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No client assigned to your account.
            </Alert>
          )}
        </Box>

        {listQuery.isLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading pre-registrations...</Typography>
          </Box>
        ) : listQuery.isError ? (
          <Alert severity="error">Failed to load pre-registrations.</Alert>
        ) : listQuery.data?.data?.length ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listQuery.data.data.map((entry) => (
                <TableRow key={entry.email}>
                  <TableCell>{entry.name || "-"}</TableCell>
                  <TableCell>{entry.email}</TableCell>
                  <TableCell>{entry.client_name || entry.client_id || "-"}</TableCell>
                  <TableCell>{formatDateTime(entry.submitted_at)}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => {
                        setSelectedEmail(entry.email);
                        setAnswersOpen(true);
                      }}
                    >
                      View answers
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => approveMutation.mutate(entry.email)}
                      disabled={approveMutation.isPending}
                    >
                      Approve
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Alert severity="info">No pending pre-registrations.</Alert>
        )}
      </Stack>

      <Dialog
        open={answersOpen}
        onClose={() => {
          setAnswersOpen(false);
          setSelectedEmail(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Pre-registration answers</DialogTitle>
        <DialogContent>
          {answersQuery.isLoading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading answers...</Typography>
            </Box>
          ) : answersQuery.isError ? (
            <Alert severity="error">Failed to load answers.</Alert>
          ) : answersQuery.data?.data ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Email: {answersQuery.data.data.email}
              </Typography>
              {answersQuery.data.data.client_name && (
                <Typography variant="body2" color="text.secondary">
                  Client: {answersQuery.data.data.client_name}
                </Typography>
              )}
              <Stack spacing={1.5}>
                {answersQuery.data.data.answers.map((answer) => {
                  const value = Array.isArray(answer.answer)
                    ? answer.answer.join(", ")
                    : answer.answer ?? "-";
                  return (
                    <Box key={answer.question_id}>
                      <Typography variant="subtitle2">
                        {answer.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {String(value)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No answers found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
